import { useEffect, useCallback } from 'react'
import { useMailStore } from '../stores/mailStore'
import { useUIStore } from '../stores/uiStore'
import { useTrashThread } from './useTrashThread'
import { useMarkThreadRead } from './useMarkThreadRead'
import { useStarThread } from './useStarThread'

// Gmail-style keyboard shortcuts
// g+i = go to inbox, g+s = go to starred, j/k = next/prev, etc.

let pendingPrefix: string | null = null
let prefixTimeout: ReturnType<typeof setTimeout> | null = null

export function useKeyboardShortcuts() {
  const store = useMailStore()
  const ui = useUIStore()
  const trashThread = useTrashThread()
  const markRead = useMarkThreadRead()
  const starThread = useStarThread()

  const handleKey = useCallback((e: KeyboardEvent) => {
    // Don't fire when focus is in an input/textarea/contenteditable
    const target = e.target as HTMLElement
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) {
      return
    }

    const key = e.key.toLowerCase()
    const ctrl = e.ctrlKey || e.metaKey

    // ─── Two-key sequences (g + ...) ──────────────────────────────────────────
    if (pendingPrefix === 'g') {
      clearTimeout(prefixTimeout!)
      pendingPrefix = null
      switch (key) {
        case 'i': store.setActiveFolder('inbox'); return
        case 's': store.setActiveFolder('starred'); return
        case 't': store.setActiveFolder('sent'); return
        case 'd': store.setActiveFolder('drafts'); return
        case 'b': store.setActiveFolder('spam'); return
      }
    }

    // ─── Single-key shortcuts ─────────────────────────────────────────────────
    switch (key) {
      case 'g':
        // Start two-key sequence
        pendingPrefix = 'g'
        prefixTimeout = setTimeout(() => { pendingPrefix = null }, 1000)
        return

      case 'c':
        if (!ctrl) {
          e.preventDefault()
          store.openCompose()
        }
        return

      case 'e':
        // Archive selected / active thread
        if (store.activeThreadId) {
          store.archiveThread(store.activeThreadId)
          ui.addToast('Archived', 'success', { duration: 2500, action: { label: 'Undo', fn: () => {} } })
        } else if (store.selectedThreadIds.size > 0) {
          store.selectedThreadIds.forEach((id) => store.archiveThread(id))
          ui.addToast(`Archived ${store.selectedThreadIds.size} conversations`, 'success')
          store.clearSelection()
        }
        return

      case '#':
        // Delete / trash
        if (store.activeThreadId) {
          const thread = store.threads.find((t) => t.id === store.activeThreadId)
          if (thread) trashThread(thread)
          ui.addToast('Moved to Trash', 'info', { duration: 2500 })
        }
        return

      case 's':
        // Star / unstar
        if (store.activeThreadId) {
          const thread = store.threads.find((t) => t.id === store.activeThreadId)
          if (thread) {
            starThread(thread.id, !thread.is_starred)
          }
        }
        return

      case 'u':
        // Mark unread
        if (store.activeThreadId) {
          markRead(store.activeThreadId, false)
          store.setActiveThread(null)
        }
        return

      case '*':
        // Select all (Gmail: * + a = all, * + n = none)
        break

      case '?':
        // Shortcuts help
        ui.openShortcutsModal()
        return

      case 'escape':
        if (store.selectedThreadIds.size > 0) {
          store.clearSelection()
        } else if (store.activeThreadId) {
          store.setActiveThread(null)
        } else if (ui.shortcutsModalOpen) {
          ui.closeShortcutsModal()
        }
        return

      case '/':
        // Focus search
        e.preventDefault()
        document.querySelector<HTMLInputElement>('[data-search-input]')?.focus()
        return
    }

    // ─── Ctrl/Cmd shortcuts ───────────────────────────────────────────────────
    if (ctrl) {
      switch (key) {
        case 'k':
          e.preventDefault()
          document.querySelector<HTMLInputElement>('[data-search-input]')?.focus()
          return
      }
    }
  }, [store, ui])

  useEffect(() => {
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [handleKey])
}

// Shortcut reference for the help modal
export const SHORTCUTS = [
  { keys: 'c',       description: 'Compose new message' },
  { keys: '/',       description: 'Search' },
  { keys: 'e',       description: 'Archive' },
  { keys: '#',       description: 'Delete / move to trash' },
  { keys: 's',       description: 'Star / unstar' },
  { keys: 'u',       description: 'Mark as unread & back' },
  { keys: 'g i',     description: 'Go to Inbox' },
  { keys: 'g s',     description: 'Go to Starred' },
  { keys: 'g t',     description: 'Go to Sent' },
  { keys: 'g d',     description: 'Go to Drafts' },
  { keys: 'g b',     description: 'Go to Spam' },
  { keys: '?',       description: 'Show keyboard shortcuts' },
  { keys: 'Esc',     description: 'Clear selection / close panel' },
  { keys: '⌘ k',    description: 'Command palette / search' },
]
